import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const emptyStudy = {
  title: '',
  subtitle: '',
  hymn: '',
  grace_sharing_text: '',
  scripture_reference: '',
  scripture_text: '',
  study_date: '',
  is_active: '', 
  country_cd: '',
  group_cd: '100',
  language: '',
};

const BibleStudyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [study, setStudy] = useState(emptyStudy);
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [answerCounts, setAnswerCounts] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!isNew) {
      loadStudy(id);
    } else {
      setStudy(emptyStudy);
      setQuestions([]);
      setDeletedQuestionIds([]);
      setAnswerCounts({});
      setLoading(false);
    }
  }, [id, isNew]);

  const loadStudy = async (studyId) => {
    setLoading(true);
    setMessage(null);
    const [{ data: studyData, error: studyError }, { data: questionData, error: questionError }] =
      await Promise.all([
        supabase.from('bible_study').select('*').eq('id', studyId).single(),
        supabase.from('bible_study_q').select('*').eq('study_id', studyId).order('seq', { ascending: true }),
      ]);

    if (studyError) {
      setMessage({ type: 'error', text: studyError.message });
      setLoading(false);
      return;
    }

    setStudy({
      ...studyData,
      study_date: toInputDate(studyData.study_date),
    });

    setQuestions(questionData || []);
    await loadAnswerCounts(studyId);
    setLoading(false);
  };

  const loadAnswerCounts = async (studyId) => {
    const { data, error } = await supabase
      .from('bible_study_a')
      .select('question_id')
      .eq('study_id', studyId);

    if (error) {
      console.error('답변 조회 오류:', error);
      return;
    }

    const counts = data.reduce((acc, curr) => {
      acc[curr.question_id] = (acc[curr.question_id] || 0) + 1;
      return acc;
    }, {});
    setAnswerCounts(counts);
  };

  const handleStudyChange = (e) => {
    const { name, value } = e.target;
    setStudy((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        tempId: createTempId(),
        study_id: study.id,
        seq: prev.length + 1,
        question: '',
        reference: '',
        reference_text: '',
        closing_text: '',
      },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => {
      const target = prev[index];
      if (target?.id) {
        setDeletedQuestionIds((ids) => [...ids, target.id]);
      }
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const studyPayload = useMemo(
    () => ({
      title: study.title.trim(),
      subtitle: study.subtitle?.trim() || null,
      hymn: study.hymn?.trim() || null,
      grace_sharing_text: study.grace_sharing_text?.trim() || null,
      scripture_reference: study.scripture_reference.trim(),
      scripture_text: study.scripture_text.trim(),
      study_date: toDbDate(study.study_date),
      language: study.language,
      country_cd: study.country_cd,
    }),
    [study]
  );

  const validateStudy = () => {
    if (!studyPayload.title || !studyPayload.scripture_reference || !studyPayload.scripture_text || !studyPayload.study_date) {
      setMessage({ type: 'error', text: '필수 항목(제목, 말씀 본문, 말씀 내용, 진행일)을 입력하세요.' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateStudy()) return;
    setSaving(true);
    setMessage(null);
    try {
      let studyId = study.id;
      if (isNew || !studyId) {
        const { data, error } = await supabase.from('bible_study').insert(studyPayload).select().single();
        if (error) throw error;
        studyId = data.id;
        setStudy((prev) => ({ ...prev, id: studyId, study_date: toInputDate(data.study_date) }));
        navigate(`/bible-study/${studyId}`, { replace: true });
      } else {
        const { error } = await supabase.from('bible_study').update(studyPayload).eq('id', studyId);
        if (error) throw error;
      }

      await syncQuestions(studyId);
      await loadAnswerCounts(studyId);
      setMessage({ type: 'success', text: '저장되었습니다.' });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: error.message || '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const syncQuestions = async (studyId) => {
    console.log(studyId, questions, deletedQuestionIds);
    if (!studyId) return;

    const preparedQuestions = questions.map((question, index) => ({
      id: question.id,
      study_id: studyId,
      seq: Number(question.seq) || index + 1,
      question: question.question?.trim(),
      reference: question.reference?.trim() || null,
      reference_text: question.reference_text?.trim() || null,
      closing_text: question.closing_text?.trim() || null,
    }));

    const newQuestionsWithTempId = preparedQuestions.filter((q) => !q.id && q.question);
    const existingQuestions = preparedQuestions.filter((q) => q.id);

    // tempId should not be sent to db
    const newQuestions = newQuestionsWithTempId.map(({ tempId, ...rest }) => rest);
    // console.log('신규 질문들:',  newQuestions);
    // 2. INSERT new questions
    if (newQuestions.length > 0) {
      // Explicitly create payload with only DB columns
      const payload = newQuestions.map((q) => ({
        study_id: q.study_id,
        seq: q.seq,
        question: q.question,
        reference: q.reference,
        reference_text: q.reference_text,
        closing_text: q.closing_text,
      }));
      // console.log('Inserting questions payload:', JSON.stringify(payload, null, 2));
      const { data: insertedData, error } = await supabase.from('bible_study_q').insert(payload).select();

      if (error) throw error;

      // Update state with new IDs from DB
      setQuestions((prev) => {
        const updatedQuestions = [...prev];
        newQuestions.forEach((tempQ) => {
          const insertedQ = insertedData.find(
            (dbQ) => dbQ.seq === tempQ.seq && dbQ.question === tempQ.question
          );
          if (insertedQ) {
            const indexInState = updatedQuestions.findIndex((q) => q.tempId === tempQ.tempId);
            if (indexInState !== -1) {
              updatedQuestions[indexInState] = insertedQ;
            }
          }
        });
        return updatedQuestions;
      });
    }
    // console.log('업데이트할 질문들:',  existingQuestions);
    if (existingQuestions.length > 0) {
      
      const { error } = await supabase.from('bible_study_q').upsert(existingQuestions);
      if (error) throw error;
    }

    if (deletedQuestionIds.length > 0) {
      const { error } = await supabase.from('bible_study_q').delete().in('id', deletedQuestionIds);
      if (error) throw error;
      setDeletedQuestionIds([]);
    }
  };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">{isNew ? '성경공부 등록' : '성경공부 상세'}</h6>
        <div>
          <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => navigate('/bible-study')}>
            목록으로
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>{message.text}</div>
        )}
        <form>
          <div className="form-row">
            <div className="col-md-6">
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>국가</label>
                  <select name="country_cd" className="form-control" value={study.country_cd || ''} onChange={handleStudyChange}>
                    <option value="">선택</option>
                    <option value="KR">한국</option>
                    <option value="US">미국</option>
                  </select>
                </div>
                <div className="form-group col-md-4">
                  <label>언어</label>
                  <select name="language" className="form-control" value={study.language || ''} onChange={handleStudyChange}>
                    <option value="">선택</option>
                    <option value="ko">ko</option>
                    <option value="en">en</option>
                    <option value="ja">ja</option>
                    <option value="zh">zh</option>
                  </select>
                </div>
                <div className="form-group col-md-4">
                  <label>진행일</label>
                  <input
                    type="date"
                    className="form-control"
                    name="study_date"
                    value={study.study_date}
                    onChange={handleStudyChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>제목</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={study.title}
                  onChange={handleStudyChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>부제목</label>
                <input
                  type="text"
                  className="form-control"
                  name="subtitle"
                  value={study.subtitle || ''}
                  onChange={handleStudyChange}
                />
              </div>
              <div className="form-group">
                <label>찬송</label>
                <input type="text" className="form-control" name="hymn" value={study.hymn || ''} onChange={handleStudyChange} />
              </div>
              <div className="form-group">
                <label>은혜 나눔 안내</label>
                <textarea
                  className="form-control"
                  name="grace_sharing_text"
                  rows="2"
                  value={study.grace_sharing_text || ''}
                  onChange={handleStudyChange}
                />
              </div>
              <div className="form-group">
                <label>말씀 본문</label>
                <input
                  type="text"
                  className="form-control"
                  name="scripture_reference"
                  value={study.scripture_reference}
                  onChange={handleStudyChange}
                  required
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>본문 내용</label>
                <textarea
                  className="form-control"
                  name="scripture_text"
                  rows="20"
                  value={study.scripture_text}
                  onChange={handleStudyChange}
                  required
                />
              </div>
            </div>
          </div>
        </form>

        <hr />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="font-weight-bold mb-0">질문 목록</h6>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddQuestion}>
            질문 추가
          </button>
        </div>

        {questions.length === 0 && <div className="text-muted mb-3">등록된 질문이 없습니다.</div>}

        {questions.map((question, index) => (
          <div className="card mb-3" key={question.id || question.tempId || `new-${index}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>질문 #{question.seq || index + 1}</strong>
                <div>
                  {answerCounts[question.id] && (
                    <span className="text-muted mr-3">답변 {answerCounts[question.id]}건</span>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleRemoveQuestion(index)}
                  >
                    삭제
                  </button>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-2">
                  <label>순번</label>
                  <input
                    type="number"
                    className="form-control"
                    value={question.seq || index + 1}
                    onChange={(e) => handleQuestionChange(index, 'seq', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-10">
                  <label>질문</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.question || ''}
                    onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>참고 구절 표기</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.reference || ''}
                    onChange={(e) => handleQuestionChange(index, 'reference', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>마무리 문구</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.closing_text || ''}
                    onChange={(e) => handleQuestionChange(index, 'closing_text', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>참고구절 본문 (reference_text)</label>
                <textarea
                  className="form-control"
                  rows={6}
                  value={question.reference_text || ''}
                  onChange={(e) => handleQuestionChange(index, 'reference_text', e.target.value)}
                  placeholder="벌크 JSON의 reference_text와 동일"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const toInputDate = (value) => {
  if (!value) return '';
  if (value.includes('-')) return value;
  if (value.length === 8) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value;
};

const toDbDate = (value) => {
  if (!value) return null;
  if (!value.includes('-')) return value;
  return value.replace(/-/g, '');
};

const createTempId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default BibleStudyDetail;

